import * as _ from 'lodash';
import * as moment from 'moment';
import * as hash from 'object-hash';
import { connect, Dispatch } from 'react-redux';
import { CellValueChangedEvent, ColumnApi, FirstDataRenderedEvent, GridApi, GridColumnsChangedEvent, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { Component, FormEvent } from 'react';
import { Button, FormControl } from 'react-bootstrap';
import { AppActionCreators, CenterActionCreators, IApplicationState, ICenterUnosDataResponse, IOptnSrtrMonthlyDataAddResponse, IUpdateOptnSrtrData } from '@nkr/store/index';
import { IBaseResponse, ICenterUnosDataMonthlyWithStatus } from '@nkr/models/index';
import { AxiosError } from 'axios';
import ConfirmDelete from '@nkr/views/admin/update-optn-srtr-data/confirm-delete';
import AddData from '@nkr/views/admin/update-optn-srtr-data/add-data';
import MonthlyData from '@nkr/views/admin/update-optn-srtr-data/monthly-data';
import mapData from './map-data';
import { IErrorItem } from '../../error-item';
import CenterProfileNav from '../edit-nav';
import { profileNavKey } from '../../profile-nav-key';
import { toast } from 'react-toastify';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterUnosData: (centerId: number) => Promise<ICenterUnosDataResponse>,
    updateCenterUnosData: (centerId: number, data: IUpdateOptnSrtrData[]) => Promise<ICenterUnosDataResponse>,
    addCenterUnosData: (data: IUpdateOptnSrtrData) => Promise<IOptnSrtrMonthlyDataAddResponse>,
    deleteCenterUnosData: (list: number[]) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: ICenterUnosDataMonthlyWithStatus[],
    hashValue: string,
    currentYear: number,
    showAdd: boolean,
    showDeleteButton: boolean,
    showDeleteConfirm: boolean,
    gridApi: GridApi,
    columnApi: ColumnApi,
}

class UnosData extends Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        hashValue: '',
        currentYear: 0,
        showAdd: false,
        showDeleteButton: false,
        showDeleteConfirm: false,
        gridApi: null,
        columnApi: null,
    }

    onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        this.setState({
            gridApi: e.api,
            columnApi: e.columnApi
        });
    }

    onRowSelected = (e: RowSelectedEvent) => {
        let rows = e.api.getSelectedNodes().filter(x => x.isSelected());

        this.setState({
            showDeleteButton: rows.length > 0
        });
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onGridColumnsChanged = (e: GridColumnsChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onCellValueChanged = (e: CellValueChangedEvent) => {
        let data = e.data as ICenterUnosDataMonthlyWithStatus;
        this.handleDataChanged(data.id, e.column.getColId(), e.newValue);
    };

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting center UNOS data, one moment please...').then(() => {
            return this.props.getCenterUnosData(this.props.centerId).then(response => {
                let sortedUnosData = _.sortBy(response.data.results, x => moment(x.dataDate, 'MM/DD/YYYY'));

                let data = sortedUnosData.map(x => {
                    let ddTxpsOriginal = x.ddTxps;
                    let ldTxpsOriginal = x.ldTxps;
                    let wlTotalOriginal = x.wlTotal;
                    let wlActiveOriginal = x.wlActive;

                    let ldaOriginal = x.lda;
                    let ldbOriginal = x.ldb;
                    let ldabOriginal = x.ldab;
                    let ldoOriginal = x.ldo;

                    let ldraOriginal = x.ldra;
                    let ldrbOriginal = x.ldrb;
                    let ldrabOriginal = x.ldrab;
                    let ldroOriginal = x.ldro;

                    return {
                        ...x,
                        ddTxpsOriginal, ldTxpsOriginal, wlTotalOriginal, wlActiveOriginal,
                        ldaOriginal, ldbOriginal, ldabOriginal, ldoOriginal,
                        ldraOriginal, ldrbOriginal, ldrabOriginal, ldroOriginal,
                        status: 0,
                        selected: false
                    };
                });

                let d = mapData(data);

                let hashValue = hash(d);

                this.props.hideLoading();

                this.setState({
                    data,
                    hashValue
                });
            });
        });
    }

    handleYearChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            currentYear: parseInt(e.currentTarget.value)
        });
    }

    handleDataChanged = (id: number, property: string, value: string) => {
        let data = _.cloneDeep(this.state.data).map(x => {
            if (x.id == id) {
                x[property] = value;

                let hasChanged =
                    x.ddTxps != x.ddTxpsOriginal
                    || x.ldTxps != x.ldTxpsOriginal
                    || x.wlTotal != x.wlTotalOriginal
                    || x.wlActive != x.wlActiveOriginal

                    || x.lda != x.ldaOriginal
                    || x.ldb != x.ldbOriginal
                    || x.ldab != x.ldabOriginal
                    || x.ldo != x.ldoOriginal

                    || x.ldra != x.ldraOriginal
                    || x.ldrb != x.ldrbOriginal
                    || x.ldrab != x.ldrabOriginal
                    || x.ldro != x.ldroOriginal
                    ;

                if (hasChanged) {
                    x.status = 1;
                } else {
                    x.status = 0;
                }
            }

            return x;
        });

        let hasDataChanged = data.filter(x => x.status == 1).length > 0;

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(hasDataChanged);
        });
    }

    handleRevertChanges = () => {
        let data = _.cloneDeep(this.state.data).map(x => {
            x.ddTxps = x.ddTxpsOriginal;
            x.ldTxps = x.ldTxpsOriginal;
            x.wlTotal = x.wlTotalOriginal;
            x.wlActive = x.wlActiveOriginal;

            x.lda = x.ldaOriginal;
            x.ldb = x.ldbOriginal;
            x.ldab = x.ldabOriginal;
            x.ldo = x.ldoOriginal;

            x.ldra = x.ldraOriginal;
            x.ldrb = x.ldrbOriginal;
            x.ldrab = x.ldrabOriginal;
            x.ldro = x.ldroOriginal;

            x.status = 0;

            return x;
        });

        this.setState({
            data
        }, () => {
            this.state.gridApi.stopEditing();
            this.props.onDataChanged(false);
        });
    }

    handleShowAdd = () => {
        this.setState({
            showAdd: true
        });
    }

    handleSaveAdd = (centerId: string, dataDate: string, ddTxps: string, ldTxps: string, wlTotal: string, wlActive: string, lda: string, ldb: string, ldab: string, ldo: string, ldra: string, ldrb: string, ldrab: string, ldro: string) => {
        this.setState({
            showAdd: false
        }, () => {
            let data: IUpdateOptnSrtrData = {
                id: 0,
                centerId: parseInt(centerId),
                dataDate,

                ddTxps,
                ldTxps,
                wlTotal,
                wlActive,

                lda,
                ldb,
                ldab,
                ldo,

                ldra,
                ldrb,
                ldrab,
                ldro
            };

            this.props.showLoading('Saving new center data, one moment please...').then(() => {
                this.props.addCenterUnosData(data).then(response => {
                    if (response.data.success) {
                        let {
                            ddTxps: ddTxpsOriginal, ldTxps: ldTxpsOriginal, wlTotal: wlTotalOriginal, wlActive: wlActiveOriginal,
                            lda: ldaOriginal, ldb: ldbOriginal, ldab: ldabOriginal, ldo: ldoOriginal,
                            ldra: ldraOriginal, ldrb: ldrbOriginal, ldrab: ldrabOriginal, ldro: ldroOriginal
                        } = response.data.results;


                        let data = _.cloneDeep(this.state.data);

                        data.push({
                            ...response.data.results,
                            ddTxpsOriginal, ldTxpsOriginal, wlTotalOriginal, wlActiveOriginal,
                            ldaOriginal, ldbOriginal, ldabOriginal, ldoOriginal,
                            ldraOriginal, ldrbOriginal, ldrabOriginal, ldroOriginal,
                            status: 0,
                            selected: false
                        });

                        let d = mapData(data);

                        let hashValue = hash(d);

                        this.setState({
                            data,
                            hashValue
                        }, () => {
                            this.props.hideLoading();
                        });
                    }

                    this.props.hideLoading();
                }).catch((error: any) => {
                    toast.error("Unable to save data. The data has already been added.");
                    this.props.hideLoading();
                    });
            })
                .catch((error: any) => {
                    this.setState({
                        // error: {
                        //     show: true,
                        //     message: error.message
                        // }
                    });
                });
        });
    }

    handleCancelAdd = () => {
        this.setState({
            showAdd: false
        });
    }

    handleShowDeleteConfirm = () => {
        this.setState({
            showDeleteConfirm: true
        });
    }

    handleDeletedSelectedRows = () => {
        let list = this.state.data.filter(x => x.selected).map(x => x.id);

        this.props.showLoading('Deleting selected records, one moment please...')
            .then(() => {
                this.props.deleteCenterUnosData(list).then(response => {
                    let data = _.cloneDeep(this.state.data).filter(x => x.selected == false);

                    this.setState({
                        data,
                        showDeleteConfirm: false
                    }, () => {
                        this.props.hideLoading();
                    });
                })
                    .catch((error: AxiosError) => {
                        // this.setState({
                        //     error: {
                        //         show: true,
                        //         message: error.message
                        //     }
                        // });
                    });
            });
    }

    handleDeleteConfirmCancel = () => {
        this.setState({
            showDeleteConfirm: false
        });
    }

    handleSavePendingChanges = () => {
        let pendingChanges = this.state.data.filter(x => x.status > 0).map(x => {
            let { id, cId, date, ddTxps, ldTxps, wlTotal, wlActive, lda, ldb, ldab, ldo, ldra, ldrb, ldrab, ldro } = x;

            return {
                id,
                centerId: cId,
                dataDate: moment(date, 'MM-YYYY').format(),

                ddTxps,
                ldTxps,
                wlTotal,
                wlActive,

                lda,
                ldb,
                ldab,
                ldo,

                ldra,
                ldrb,
                ldrab,
                ldro
            };
        });

        this.props.showLoading('Updating data, one moment please...').then(() => {
            this.props.updateCenterUnosData(this.props.centerId, pendingChanges).then(x => {
                let data = _.cloneDeep(this.state.data).map(x => {
                    x.ddTxpsOriginal = x.ddTxps;
                    x.ldTxpsOriginal = x.ldTxps;
                    x.wlTotalOriginal = x.wlTotal;
                    x.wlActiveOriginal = x.wlActive;

                    x.ldaOriginal = x.lda;
                    x.ldbOriginal = x.ldb;
                    x.ldabOriginal = x.ldab;
                    x.ldoOriginal = x.ldo;

                    x.ldraOriginal = x.ldra;
                    x.ldrbOriginal = x.ldrb;
                    x.ldrabOriginal = x.ldrab;
                    x.ldroOriginal = x.ldro;

                    x.status = 0

                    return x;
                });

                let d = mapData(data);

                let hashValue = hash(d);

                this.setState({
                    data,
                    hashValue
                }, () => {
                    this.props.onDataChanged(false);
                    this.props.hideLoading();

                    this.state.gridApi.stopEditing(true);
                });
            });
        });
    }

    getFilteredData = () => {
        let filteredData = this.state.data.filter(x => {
            let yearFilter = this.state.currentYear > 0 ? moment(x.dataDate, 'YYYY-MM-DD').year() == this.state.currentYear : true;

            return yearFilter;
        });

        return filteredData;
    }

    getSelectedData = () => {
        let selectedRows = this.state.gridApi != null ? this.state.gridApi.getSelectedNodes().filter(x => x.isSelected()).map(x => x.data) : [];
        return _.orderBy(selectedRows, x => moment(x.date, 'MM-YYYY'));
    }

    render = () => {
        const rowData = this.getFilteredData();
        const deleteConfirmData = this.getSelectedData();

        const years = _.orderBy(_.uniq(this.state.data.map(x => moment(x.dataDate, 'YYYY-MM-DD').get('year'))), year => year, 'desc');

        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.UNOS}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.handleSavePendingChanges}
                    onRevert={this.handleRevertChanges}
                />

                <div style={{ padding: 10, borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                            <div style={{ marginRight: 5 }}>Filter by Year:</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    value={this.state.currentYear}
                                    onChange={this.handleYearChange}
                                >
                                    <option value={0}>- All Years -</option>
                                    {years.map(year =>
                                        <option key={'yr-' + year} value={year}>{year}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>

                        <div>
                            <Button onClick={this.handleShowAdd}>Add Data for Center</Button>

                            {this.state.showDeleteButton && (
                                <Button bsStyle='danger' style={{ marginLeft: 10 }} onClick={this.handleShowDeleteConfirm}>Delete Selected Rows</Button>
                            )}
                        </div>
                    </div>
                </div>

                <MonthlyData
                    data={rowData}
                    centers={[]}
                    loading={false}
                    isCenter={true}
                    gridHeight='60vh'
                    onGridReady={this.onGridReady}
                    onRowSelected={this.onRowSelected}
                    onDataChanged={this.handleDataChanged}
                    onSavePendingChanges={this.handleSavePendingChanges}
                    onAddData={this.handleShowAdd}
                    onDeleteData={this.handleShowDeleteConfirm}
                />

                <AddData
                    show={this.state.showAdd}
                    centers={[]}
                    centerId={this.props.centerId}
                    showCenter={false}
                    onSave={this.handleSaveAdd}
                    onCancel={this.handleCancelAdd}
                />

                <ConfirmDelete
                    show={this.state.showDeleteConfirm}
                    data={deleteConfirmData}
                    isCenter={true}
                    onDelete={this.handleDeletedSelectedRows}
                    onCancel={this.handleDeleteConfirmCancel}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCenterUnosData: (centerId) => {
            return dispatch(CenterActionCreators.getCenterUnosData(centerId));
        },
        updateCenterUnosData: (centerId, data) => {
            return dispatch(CenterActionCreators.updateCenterUnosData(centerId, data))
        },
        addCenterUnosData: (data) => {
            return dispatch(CenterActionCreators.addOptnSrtrData(data));
        },
        deleteCenterUnosData: (list) => {
            return dispatch(CenterActionCreators.deleteOptnSrtrData(list));
        }
    }
}

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(UnosData);